import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  useTheme,
  TableSortLabel,
  Collapse,
  Tooltip,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { FilterContext } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import NewsletterAdsDetailModal from './NewsletterAdsDetailModal';

interface NewsletterAd {
  history_id: number;
  ad_id: number;
  ad_control_code: number;
  campaign_id: string;
  it_campaign_id: number | null;
  nl_id: number;
  send_date: string;
  subject: string;
  email_subject: string;
  from_name: string;
  form: string;
  product_name: string;
  product_customer: string;
  newsletter_name: string;
  esp_type: string;
  recipients: number;
  opens: number;
  clicks: number;
  leads: number;
  turnover: number;
  total_revenue: number;
  open_rate: number;
  click_rate: number;
  lead_rate: number;
  etko: number;
  etkp: number;
}

interface Filters {
  ad_control_codes: number[];
  ad_control_code_definitions: { [key: number]: string };
  customers: string[];
  forms: string[];
  newsletters: string[];
}

interface ApiSummary {
  total_recipients: number;
  total_recipients_unique: number; // Deduplicated recipients for accurate ETKP
  total_opens: number;
  total_opens_unique: number; // Deduplicated opens for accurate ETKO calculation
  total_clicks: number;
  total_leads: number;
  total_revenue: number;
  unique_send_count: number;
}

type OrderBy = keyof NewsletterAd;
type Order = 'asc' | 'desc';

const NewsletterAdsTimelineTable: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [data, setData] = useState<NewsletterAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [selectedAd, setSelectedAd] = useState<NewsletterAd | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [apiSummary, setApiSummary] = useState<ApiSummary | null>(null);

  // Filters
  const [filters, setFilters] = useState<Filters>({
    ad_control_codes: [],
    ad_control_code_definitions: {},
    customers: [],
    forms: [],
    newsletters: [],
  });
  const [showLegend, setShowLegend] = useState(true);
  const [selectedControlCodes, setSelectedControlCodes] = useState<number[]>([0]);
  const [selectedNewsletters, setSelectedNewsletters] = useState<string[]>([]);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [selectedEsp, setSelectedEsp] = useState<string>('all');
  const [minRecipients, setMinRecipients] = useState<number>(10);

  // Sorting
  const [orderBy, setOrderBy] = useState<OrderBy>('send_date');
  const [order, setOrder] = useState<Order>('desc');

  // Load available filters
  useEffect(() => {
    const loadFilters = async () => {
      if (!filterState?.startDate || !filterState?.endDate) {
        return;
      }

      try {
        // Format dates to YYYY-MM-DD
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const options = {
          start_date: formatDate(filterState.startDate),
          end_date: formatDate(filterState.endDate),
        };

        const response = (await fetchData(
          '/api/newsletter-ads-timeline/filters',
          options,
          false,
        )) as any;
        const newFilters = {
          ad_control_codes: response.ad_control_codes || [],
          ad_control_code_definitions: response.ad_control_code_definitions || {},
          customers: response.customers || [],
          forms: response.forms || [],
          newsletters: response.newsletters || [],
        };

        setFilters(newFilters);

        // Reset selected control codes if they're no longer available
        const availableCodes = newFilters.ad_control_codes;
        const validSelectedCodes = selectedControlCodes.filter((code) =>
          availableCodes.includes(code),
        );

        // If no valid codes remain, default to [0] if available, otherwise first available code
        if (validSelectedCodes.length === 0) {
          if (availableCodes.includes(0)) {
            setSelectedControlCodes([0]);
          } else if (availableCodes.length > 0) {
            setSelectedControlCodes([availableCodes[0]]);
          }
        } else if (validSelectedCodes.length !== selectedControlCodes.length) {
          setSelectedControlCodes(validSelectedCodes);
        }
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    };
    loadFilters();
  }, [filterState?.startDate, filterState?.endDate]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!filterState?.startDate || !filterState?.endDate) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Format dates to YYYY-MM-DD
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const options: any = {
          start_date: formatDate(filterState.startDate),
          end_date: formatDate(filterState.endDate),
          esp: selectedEsp,
          newsletter: selectedNewsletters.length > 0 ? selectedNewsletters : undefined,
          form: selectedForms.length > 0 ? selectedForms : undefined,
          min_recipients: minRecipients,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          ad_control_code: selectedControlCodes,
          customer_id:
            filterState.customers && filterState.customers.length > 0
              ? filterState.customers
              : undefined,
        };

        const response = (await fetchData('/api/newsletter-ads-timeline', options, false)) as any;

        if (response.data) {
          setData(response.data);
          setTotal(response.pagination?.total || 0);
          setApiSummary(response.summary || null);
        }
      } catch (err: any) {
        console.error('Error loading newsletter ads:', err);
        setError(err.message || 'Failed to load newsletter ads');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState,
    selectedControlCodes,
    selectedNewsletters,
    selectedForms,
    selectedEsp,
    minRecipients,
    page,
    rowsPerPage,
  ]);

  const handleControlCodeChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedControlCodes(typeof value === 'string' ? [parseInt(value)] : value);
    setPage(0);
  };

  const handleEspChange = (event: SelectChangeEvent) => {
    setSelectedEsp(event.target.value);
    setPage(0);
  };

  const handleNewsletterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedNewsletters(typeof value === 'string' ? [value] : value);
    setPage(0);
  };

  const handleFormChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedForms(typeof value === 'string' ? [value] : value);
    setPage(0);
  };

  const handleRowClick = (ad: NewsletterAd) => {
    setSelectedAd(ad);
    setModalOpen(true);
  };

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === null || aValue === undefined) return order === 'asc' ? 1 : -1;
      if (bValue === null || bValue === undefined) return order === 'asc' ? -1 : 1;

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [data, order, orderBy]);

  // Use API summary instead of calculating from paginated data
  // This fixes the "pagination dilution" bug where selecting more control codes
  // appeared to show fewer leads because the summary was calculated from paginated data
  const summaryData = React.useMemo(() => {
    if (!apiSummary) return null;

    const totalRecipients = apiSummary.total_recipients;
    const totalRecipientsUnique = apiSummary.total_recipients_unique; // Deduplicated for ETKP
    const totalOpens = apiSummary.total_opens;
    const totalOpensUnique = apiSummary.total_opens_unique; // Deduplicated for ETKO
    const totalClicks = apiSummary.total_clicks;
    const totalLeads = apiSummary.total_leads;
    const totalRevenue = apiSummary.total_revenue;
    const uniqueSendCount = apiSummary.unique_send_count;

    // Calculate weighted averages for rates
    const avgOpenRate = totalRecipients > 0 ? (totalOpens / totalRecipients) * 100 : 0;
    const avgClickRate = totalOpens > 0 ? (totalClicks / totalOpens) * 100 : 0;
    const avgLeadRate = totalOpens > 0 ? (totalLeads / totalOpens) * 100 : 0;
    // Use unique opens / recipients to avoid overcounting (campaign metrics duplicated per ad)
    const avgEtko = totalOpensUnique > 0 ? (totalRevenue / totalOpensUnique) * 1000 : 0;
    const avgEtkp = totalRecipientsUnique > 0 ? (totalRevenue / totalRecipientsUnique) * 1000 : 0;

    return {
      newsletter_name: 'Insg.',
      recipients: totalRecipients,
      opens: totalOpens,
      clicks: totalClicks,
      leads: totalLeads,
      total_revenue: totalRevenue,
      open_rate: avgOpenRate,
      click_rate: avgClickRate,
      lead_rate: avgLeadRate,
      etko: avgEtko,
      etkp: avgEtkp,
      unique_send_count: uniqueSendCount,
    } as Partial<NewsletterAd> & { unique_send_count: number };
  }, [apiSummary]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return '-';
    return numValue.toLocaleString('de-DE');
  };

  const formatRate = (rate: number | null | undefined) => {
    if (rate === null || rate === undefined) return '-';
    const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (isNaN(numRate)) return '-';
    return `${numRate.toFixed(2)}%`;
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '-';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '-';
    return `€${numAmount.toFixed(2)}`;
  };

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>
            Ad Control Codes
            <Tooltip title="Codes show which customers users are NOT subscribed to yet. See legend below for details.">
              <Info fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
            </Tooltip>
          </InputLabel>
          <Select
            multiple
            value={selectedControlCodes}
            onChange={handleControlCodeChange}
            input={<OutlinedInput label="Ad Control Codes" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {filters.ad_control_codes.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>ESP</InputLabel>
          <Select value={selectedEsp} onChange={handleEspChange} label="ESP">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="mailchimp">Mailchimp</MenuItem>
            <MenuItem value="iterable">Iterable</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Newsletter</InputLabel>
          <Select
            multiple
            value={selectedNewsletters}
            onChange={handleNewsletterChange}
            input={<OutlinedInput label="Newsletter" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {filters.newsletters.map((newsletter) => (
              <MenuItem key={newsletter} value={newsletter}>
                {newsletter}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Form</InputLabel>
          <Select
            multiple
            value={selectedForms}
            onChange={handleFormChange}
            input={<OutlinedInput label="Form" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {filters.forms.map((form) => (
              <MenuItem key={form} value={form}>
                {form}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Min Recipients</InputLabel>
          <Select
            value={minRecipients}
            onChange={(e) => {
              setMinRecipients(Number(e.target.value));
              setPage(0);
            }}
            label="Min Recipients"
          >
            <MenuItem value={1}>All</MenuItem>
            <MenuItem value={10}>≥ 10</MenuItem>
            <MenuItem value={100}>≥ 100</MenuItem>
            <MenuItem value={1000}>≥ 1000</MenuItem>
            <MenuItem value={10000}>≥ 10000</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Control Code Legend */}
      {filters.ad_control_codes.length > 0 && (
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6" fontSize="1rem">
              Ad Control Code Legend
            </Typography>
            <Tooltip title="Control codes show which customers users are NOT subscribed to yet. Code 0 = not subscribed to any customer (default ad)">
              <Info fontSize="small" color="action" />
            </Tooltip>
            <Typography
              variant="body2"
              sx={{
                cursor: 'pointer',
                color: colors.grey[100],
                '&:hover': { color: colors.primary[100] },
              }}
              onClick={() => setShowLegend(!showLegend)}
            >
              {showLegend ? 'Hide' : 'Show'}
            </Typography>
          </Box>
          <Collapse in={showLegend}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
              }}
            >
              <Box display="flex" flexWrap="wrap" gap={1}>
                {filters.ad_control_codes.map((code) => (
                  <Box key={code} display="flex" alignItems="center" gap={1}>
                    <Chip label={code} size="small" color={code === 0 ? 'primary' : 'default'} />
                    <Typography variant="body2">
                      = {filters.ad_control_code_definitions[code] || 'Unknown'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Collapse>
        </Box>
      )}

      {/* Average Metrics */}
      {summaryData && data.length > 0 && (
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <Tooltip title="Average recipients per newsletter send. Each newsletter send (same date + name) is counted once, regardless of how many control codes are displayed.">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Recipients
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(
                  Math.round((summaryData.recipients || 0) / (summaryData.unique_send_count || 1)),
                )}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Average opens per newsletter send. Sum of opens across all control codes ÷ number of unique newsletter sends (same date + name).">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Opens
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(
                  Math.round((summaryData.opens || 0) / (summaryData.unique_send_count || 1)),
                )}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Average clicks per newsletter send. Sum of clicks across all control codes ÷ number of unique newsletter sends (same date + name).">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Clicks
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {((summaryData.clicks || 0) / (summaryData.unique_send_count || 1)).toFixed(2)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Average leads per newsletter send. Sum of leads across all control codes ÷ number of unique newsletter sends (same date + name).">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Leads
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {((summaryData.leads || 0) / (summaryData.unique_send_count || 1)).toFixed(2)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Weighted average open rate across all displayed ads. Calculated as: Total opens ÷ Total recipients × 100. Not grouped by newsletter - weighted by recipient volume.">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Open Rate
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatRate(summaryData.open_rate)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Weighted average click rate across all displayed ads. Calculated as: Total clicks ÷ Total opens × 100. Measures ad effectiveness among engaged audience.">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Click Rate
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatRate(summaryData.click_rate)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Weighted average lead rate across all displayed ads. Calculated as: Total leads ÷ Total opens × 100. Measures conversion rate among engaged audience.">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Lead Rate
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatRate(summaryData.lead_rate)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Average revenue per newsletter send. Sum of revenue across all control codes ÷ number of unique newsletter sends (same date + name).">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Revenue
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(
                  (summaryData.total_revenue || 0) / (summaryData.unique_send_count || 1),
                )}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Weighted average ETKO (Euro per 1000 opens) across all displayed ads. Calculated as: Total revenue ÷ Total opens × 1000. Measures revenue efficiency per engaged audience.">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø ETKO
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(summaryData.etko || 0)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Ø ETKP (Erlös pro 1000 Empfänger). Berechnet: Gesamt-Revenue ÷ unique Empfänger × 1000. Misst die Monetarisierungs-Effizienz pro Reichweite — niedriger als ETKO, weil nicht alle Empfänger die Mail geöffnet haben.">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø ETKP
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(summaryData.etkp || 0)}
              </Typography>
            </Paper>
          </Tooltip>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'send_date'}
                  direction={orderBy === 'send_date' ? order : 'asc'}
                  onClick={() => handleSort('send_date')}
                >
                  Send Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Newsletter</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'ad_control_code'}
                  direction={orderBy === 'ad_control_code' ? order : 'asc'}
                  onClick={() => handleSort('ad_control_code')}
                >
                  Control Code
                </TableSortLabel>
              </TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>ESP</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'form'}
                  direction={orderBy === 'form' ? order : 'asc'}
                  onClick={() => handleSort('form')}
                >
                  Form
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'recipients'}
                  direction={orderBy === 'recipients' ? order : 'asc'}
                  onClick={() => handleSort('recipients')}
                >
                  Recipients
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'opens'}
                  direction={orderBy === 'opens' ? order : 'asc'}
                  onClick={() => handleSort('opens')}
                >
                  Opens
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'clicks'}
                  direction={orderBy === 'clicks' ? order : 'asc'}
                  onClick={() => handleSort('clicks')}
                >
                  Clicks
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'leads'}
                  direction={orderBy === 'leads' ? order : 'asc'}
                  onClick={() => handleSort('leads')}
                >
                  Leads
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Opens ÷ Recipients × 100. Measures email engagement rate.">
                  <TableSortLabel
                    active={orderBy === 'open_rate'}
                    direction={orderBy === 'open_rate' ? order : 'asc'}
                    onClick={() => handleSort('open_rate')}
                  >
                    Open Rate
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Clicks ÷ Opens × 100. Measures ad effectiveness among engaged audience.">
                  <TableSortLabel
                    active={orderBy === 'click_rate'}
                    direction={orderBy === 'click_rate' ? order : 'asc'}
                    onClick={() => handleSort('click_rate')}
                  >
                    Click Rate
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Leads ÷ Opens × 100. Measures conversion rate among engaged audience.">
                  <TableSortLabel
                    active={orderBy === 'lead_rate'}
                    direction={orderBy === 'lead_rate' ? order : 'asc'}
                    onClick={() => handleSort('lead_rate')}
                  >
                    Lead Rate
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'total_revenue'}
                  direction={orderBy === 'total_revenue' ? order : 'asc'}
                  onClick={() => handleSort('total_revenue')}
                >
                  Revenue
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Revenue ÷ Opens × 1000. Euro per thousand opens - measures revenue efficiency per engaged audience.">
                  <TableSortLabel
                    active={orderBy === 'etko'}
                    direction={orderBy === 'etko' ? order : 'asc'}
                    onClick={() => handleSort('etko')}
                  >
                    ETKO
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Revenue ÷ Empfänger × 1000. Euro pro 1000 Empfänger — Reichweiten-Effizienz (immer ≤ ETKO).">
                  <TableSortLabel
                    active={orderBy === 'etkp'}
                    direction={orderBy === 'etkp' ? order : 'asc'}
                    onClick={() => handleSort('etkp')}
                  >
                    ETKP
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryData && (
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark' ? colors.primary[700] : colors.grey[300],
                  fontWeight: 'bold',
                  '& td': {
                    fontWeight: 'bold !important',
                    borderBottom: '2px solid',
                    borderColor:
                      theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[500],
                    color: '#ffffff !important',
                  },
                }}
              >
                <TableCell>-</TableCell>
                <TableCell>{summaryData.newsletter_name}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell align="right">Σ {formatNumber(summaryData.recipients)}</TableCell>
                <TableCell align="right">Σ {formatNumber(summaryData.opens)}</TableCell>
                <TableCell align="right">Σ {formatNumber(summaryData.clicks)}</TableCell>
                <TableCell align="right">Σ {formatNumber(summaryData.leads)}</TableCell>
                <TableCell align="right">Ø {formatRate(summaryData.open_rate)}</TableCell>
                <TableCell align="right">Ø {formatRate(summaryData.click_rate)}</TableCell>
                <TableCell align="right">Ø {formatRate(summaryData.lead_rate)}</TableCell>
                <TableCell align="right">
                  Σ {formatCurrency(summaryData.total_revenue || 0)}
                </TableCell>
                <TableCell align="right">Ø {formatCurrency(summaryData.etko || 0)}</TableCell>
                <TableCell align="right">Ø {formatCurrency(summaryData.etkp || 0)}</TableCell>
              </TableRow>
            )}
            {sortedData.map((ad) => (
              <TableRow
                key={ad.history_id}
                hover
                onClick={() => handleRowClick(ad)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{formatDate(ad.send_date)}</TableCell>
                <TableCell>{ad.newsletter_name}</TableCell>
                <TableCell>{ad.email_subject || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={ad.ad_control_code}
                    size="small"
                    color={ad.ad_control_code === 0 ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>{ad.product_customer || '-'}</TableCell>
                <TableCell>{ad.product_name || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={ad.esp_type}
                    size="small"
                    color={ad.esp_type === 'mailchimp' ? 'primary' : 'secondary'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={ad.form || '-'}
                    size="small"
                    color={
                      ad.form === 'html'
                        ? 'error'
                        : ad.form === 'native'
                          ? 'success'
                          : ad.form === 'checkpoint'
                            ? 'warning'
                            : 'default'
                    }
                  />
                </TableCell>
                <TableCell align="right">{formatNumber(ad.recipients)}</TableCell>
                <TableCell align="right">{formatNumber(ad.opens)}</TableCell>
                <TableCell align="right">{formatNumber(ad.clicks)}</TableCell>
                <TableCell align="right">{formatNumber(ad.leads)}</TableCell>
                <TableCell align="right">{formatRate(ad.open_rate)}</TableCell>
                <TableCell align="right">{formatRate(ad.click_rate)}</TableCell>
                <TableCell align="right">{formatRate(ad.lead_rate)}</TableCell>
                <TableCell align="right">{formatCurrency(ad.total_revenue)}</TableCell>
                <TableCell align="right">{formatCurrency(ad.etko)}</TableCell>
                <TableCell align="right">{formatCurrency(ad.etkp)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200, 500]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Detail Modal */}
      {selectedAd && (
        <NewsletterAdsDetailModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedAd(null);
          }}
          adHistoryId={selectedAd.history_id}
        />
      )}
    </Box>
  );
};

export default NewsletterAdsTimelineTable;
